import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import SessionPage from './pages/SessionPage';
import RewardsPage from './pages/RewardsPage';
import CoinWorldPage from './pages/CoinWorldPage';
import VaultPage from './pages/VaultPage';
import GardenPage from './pages/GardenPage';
import MarketPage from './pages/MarketPage';
import CollectionsPage from './pages/CollectionsPage';
import WeeklySummaryPage from './pages/WeeklySummaryPage';
import SkillGardenPage from './pages/SkillGardenPage';
import AdminPage from './pages/AdminPage';
import CollectionStorePage from './pages/CollectionStorePage';
import RiddlePage from './pages/RiddlePage';
import PetWorldPage from './pages/PetWorldPage';
import RealPrizesPage from './pages/RealPrizesPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import CognitivePassportPage from './pages/CognitivePassportPage';
import './App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <img src="/brain-gym-logo.png" alt="Loading" className="w-32 h-32 mx-auto mb-4 animate-bounce" />
          <p className="text-xl text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <img src="/brain-gym-logo.png" alt="Loading" className="w-32 h-32 mx-auto mb-4 animate-bounce-soft" />
          <p className="text-xl text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    if (!user.onboarding_complete) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Onboarding Route wrapper
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <img src="/brain-gym-logo.png" alt="Loading" className="w-32 h-32 mx-auto mb-4 animate-bounce-soft" />
          <p className="text-xl text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.onboarding_complete) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Admin Route - Public (has its own auth) */}
      <Route path="/admin" element={<AdminPage />} />

      {/* Public Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />

      {/* Onboarding Route */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session"
        element={
          <ProtectedRoute>
            <SessionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/skill-garden"
        element={
          <ProtectedRoute>
            <SkillGardenPage />
          </ProtectedRoute>
        }
      />

      {/* Coin World Routes */}
      <Route
        path="/coin-world"
        element={
          <ProtectedRoute>
            <CoinWorldPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/vault"
        element={
          <ProtectedRoute>
            <VaultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/garden"
        element={
          <ProtectedRoute>
            <GardenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/market"
        element={
          <ProtectedRoute>
            <MarketPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/collections"
        element={
          <ProtectedRoute>
            <PetWorldPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/pets"
        element={
          <ProtectedRoute>
            <PetWorldPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coin-world/summary"
        element={
          <ProtectedRoute>
            <WeeklySummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/collection-store"
        element={
          <ProtectedRoute>
            <CollectionStorePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/riddles"
        element={
          <ProtectedRoute>
            <RiddlePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/real-prizes"
        element={
          <ProtectedRoute>
            <RealPrizesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account-settings"
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cognitive-passport"
        element={
          <ProtectedRoute>
            <CognitivePassportPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App font-nunito">
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            toastOptions={{
              className: 'font-nunito',
              style: {
                borderRadius: '1rem',
              }
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
